import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, Clock, CalendarDays, ChevronRight,
  CircleDot, Coffee, CheckCircle2,
  Phone, ArrowLeft, RefreshCw, Timer,
  Star, TrendingUp, BarChart3,
  LogIn, LogOut, Pause, Play
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const STATUS_CONFIG = {
  active: { label: 'En service', color: 'bg-emerald-500', text: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  on_break: { label: 'En pause', color: 'bg-amber-500', text: 'text-amber-400', bg: 'bg-amber-500/10' },
  absent: { label: 'Absent', color: 'bg-slate-500', text: 'text-slate-400', bg: 'bg-slate-500/10' },
  completed: { label: 'Termine', color: 'bg-blue-500', text: 'text-blue-400', bg: 'bg-blue-500/10' },
};

const fmtTime = (iso) => {
  if (!iso) return '-';
  try { return format(parseISO(iso), 'HH:mm'); } catch { return '-'; }
};

const StatCard = ({ icon: Icon, value, label, accent }) => (
  <div
    className="relative overflow-hidden rounded-2xl p-5 border border-white/[0.06]"
    style={{ background: 'rgba(30, 41, 59, 0.65)', backdropFilter: 'blur(16px)' }}
    data-testid={`stat-card-${label.toLowerCase().replace(/\s/g, '-')}`}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className={`text-3xl font-bold tracking-tight ${accent}`}>{value}</p>
        <p className="text-xs text-slate-400 mt-1 uppercase tracking-wider font-medium">{label}</p>
      </div>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accent.replace('text-', 'bg-').replace('400', '500/15')}`}>
        <Icon className={`w-5 h-5 ${accent}`} />
      </div>
    </div>
  </div>
);

const RatingStars = ({ rating, size = 'w-4 h-4' }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map(i => (
      <Star
        key={i}
        className={`${size} ${i <= rating ? 'text-amber-400 fill-amber-400' : 'text-slate-600'}`}
      />
    ))}
  </div>
);

const AdminMobileDashboard = ({ auth, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [summaryView, setSummaryView] = useState('weekly');

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 30000);
    return () => clearInterval(timer);
  }, []);

  const loadData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/mobile-dashboard`);
      setData(res.data);
    } catch (err) {
      toast.error('Erreur de chargement du dashboard');
      console.error(err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadData();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-400">Chargement...</p>
        </div>
      </div>
    );
  }

  const stats = data?.stats || {};
  const employees = data?.employees || [];
  const appointments = data?.appointments || [];
  const weeklySummary = data?.weekly_summary || [];
  const monthlySummary = data?.monthly_summary || [];
  const satisfaction = data?.satisfaction || {};

  const statusOrder = { active: 0, on_break: 1, completed: 2, absent: 3 };
  const sortedEmployees = [...employees].sort((a, b) => (statusOrder[a.status] ?? 4) - (statusOrder[b.status] ?? 4));

  const tabs = [
    { id: 'overview', label: 'Vue generale', icon: BarChart3 },
    { id: 'pointage', label: 'Pointage', icon: Clock },
    { id: 'agenda', label: 'RDV du jour', icon: CalendarDays },
    { id: 'summaries', label: 'Resumes', icon: TrendingUp },
    { id: 'satisfaction', label: 'Satisfaction', icon: Star },
  ];

  return (
    <div className="min-h-screen bg-slate-900" data-testid="admin-mobile-dashboard">
      {/* Header */}
      <div
        className="sticky top-0 z-20 border-b border-white/[0.06]"
        style={{ background: 'rgba(15, 23, 42, 0.92)', backdropFilter: 'blur(16px)' }}
      >
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={onBack}
                className="w-9 h-9 rounded-lg bg-white/[0.06] flex items-center justify-center hover:bg-white/10 transition-colors"
                data-testid="admin-dashboard-back-btn"
              >
                <ArrowLeft className="w-4 h-4 text-slate-300" />
              </button>
              <div>
                <h1 className="text-lg font-bold text-white tracking-tight">Tableau de bord</h1>
                <p className="text-xs text-slate-500">
                  {format(currentTime, "EEEE d MMMM yyyy · HH:mm", { locale: fr })}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              className="text-slate-400 hover:text-white hover:bg-white/[0.06]"
              data-testid="refresh-dashboard-btn"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {/* Tab bar */}
          <div className="flex gap-1 mt-4 -mb-px overflow-x-auto" data-testid="dashboard-tabs">
            {tabs.map(tab => {
              const TabIcon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg transition-all whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'bg-white/[0.08] text-white'
                      : 'text-slate-500 hover:text-slate-300'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <TabIcon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">

        {/* ===== OVERVIEW TAB ===== */}
        {activeTab === 'overview' && (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="stats-grid">
              <StatCard icon={CircleDot} value={stats.active || 0} label="En service" accent="text-emerald-400" />
              <StatCard icon={Coffee} value={stats.on_break || 0} label="En pause" accent="text-amber-400" />
              <StatCard icon={CheckCircle2} value={stats.completed || 0} label="Termine" accent="text-blue-400" />
              <StatCard icon={CalendarDays} value={stats.appointments_today || 0} label="RDV aujourd'hui" accent="text-cyan-400" />
            </div>

            {/* Employee quick view table */}
            <div
              className="rounded-2xl border border-white/[0.06] overflow-hidden"
              style={{ background: 'rgba(30, 41, 59, 0.5)', backdropFilter: 'blur(12px)' }}
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-emerald-400" />
                  <h3 className="text-sm font-bold text-white uppercase tracking-wider">Equipe du jour</h3>
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400">{employees.length}</span>
                </div>
                <button
                  onClick={() => setActiveTab('pointage')}
                  className="text-xs text-slate-400 hover:text-white flex items-center gap-1 transition-colors"
                  data-testid="goto-pointage-tab"
                >
                  Detail <ChevronRight className="w-3 h-3" />
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="overview-employee-table">
                  <thead>
                    <tr className="text-xs text-slate-500 uppercase tracking-wider border-b border-white/[0.04]">
                      <th className="text-left px-5 py-3 font-medium">Employe</th>
                      <th className="text-center px-3 py-3 font-medium">Statut</th>
                      <th className="text-center px-3 py-3 font-medium">Arrivee</th>
                      <th className="text-center px-3 py-3 font-medium">Depart</th>
                      <th className="text-right px-5 py-3 font-medium">Heures</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.04]">
                    {sortedEmployees.map(emp => {
                      const cfg = STATUS_CONFIG[emp.status] || STATUS_CONFIG.absent;
                      return (
                        <tr key={emp.employee_id} className="hover:bg-white/[0.02] transition-colors" data-testid={`overview-emp-row-${emp.employee_id}`}>
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${cfg.bg} ${cfg.text}`}>
                                {emp.name?.charAt(0)?.toUpperCase()}
                              </div>
                              <span className="text-white font-medium">{emp.name}</span>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${cfg.color}`} />
                              {cfg.label}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-center font-mono text-slate-300 text-xs">{fmtTime(emp.clock_in)}</td>
                          <td className="px-3 py-3 text-center font-mono text-slate-300 text-xs">{fmtTime(emp.clock_out)}</td>
                          <td className="px-5 py-3 text-right font-mono text-white text-xs font-semibold">
                            {emp.total_hours != null ? `${emp.total_hours.toFixed(1)}h` : '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {employees.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-sm text-slate-500">Aucun employe actif</p>
                  </div>
                )}
              </div>
            </div>

            {/* Today's appointments preview */}
            {appointments.length > 0 && (
              <div
                className="rounded-2xl border border-white/[0.06] overflow-hidden"
                style={{ background: 'rgba(30, 41, 59, 0.5)', backdropFilter: 'blur(12px)' }}
              >
                <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
                  <div className="flex items-center gap-2">
                    <CalendarDays className="w-4 h-4 text-cyan-400" />
                    <h3 className="text-sm font-bold text-white uppercase tracking-wider">RDV aujourd'hui</h3>
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400">{appointments.length}</span>
                  </div>
                  <button
                    onClick={() => setActiveTab('agenda')}
                    className="text-xs text-slate-400 hover:text-white flex items-center gap-1 transition-colors"
                    data-testid="goto-agenda-tab"
                  >
                    Voir tout <ChevronRight className="w-3 h-3" />
                  </button>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 p-4">
                  {appointments.slice(0, 3).map(appt => (
                    <AppointmentCard key={appt.id} appt={appt} />
                  ))}
                </div>
              </div>
            )}

            {/* Satisfaction mini */}
            <div
              className="rounded-2xl border border-white/[0.06] overflow-hidden"
              style={{ background: 'rgba(30, 41, 59, 0.5)', backdropFilter: 'blur(12px)' }}
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-amber-400" />
                  <h3 className="text-sm font-bold text-white uppercase tracking-wider">Satisfaction</h3>
                </div>
                <button
                  onClick={() => setActiveTab('satisfaction')}
                  className="text-xs text-slate-400 hover:text-white flex items-center gap-1 transition-colors"
                  data-testid="goto-satisfaction-tab"
                >
                  Detail <ChevronRight className="w-3 h-3" />
                </button>
              </div>
              <div className="px-5 py-4 flex items-center gap-8">
                <div className="text-center">
                  <p className="text-3xl font-bold text-amber-400">{satisfaction.avg_rating || '-'}</p>
                  <RatingStars rating={Math.round(satisfaction.avg_rating || 0)} />
                  <p className="text-xs text-slate-500 mt-1">{satisfaction.completed || 0} avis</p>
                </div>
                <div className="flex-1 grid grid-cols-2 gap-3">
                  <div className="text-center p-3 rounded-xl bg-white/[0.03]">
                    <p className="text-lg font-bold text-white">{satisfaction.response_rate || 0}%</p>
                    <p className="text-xs text-slate-500">Taux de reponse</p>
                  </div>
                  <div className="text-center p-3 rounded-xl bg-white/[0.03]">
                    <p className="text-lg font-bold text-white">{satisfaction.pending || 0}</p>
                    <p className="text-xs text-slate-500">En attente</p>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ===== POINTAGE TAB ===== */}
        {activeTab === 'pointage' && (
          <div
            className="rounded-2xl border border-white/[0.06] overflow-hidden"
            style={{ background: 'rgba(30, 41, 59, 0.5)', backdropFilter: 'blur(12px)' }}
            data-testid="pointage-section"
          >
            <div className="px-5 py-4 border-b border-white/[0.06]">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-cyan-400" />
                <h3 className="text-sm font-bold text-white uppercase tracking-wider">Pointage detaille — Aujourd'hui</h3>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="pointage-table">
                <thead>
                  <tr className="text-xs text-slate-500 uppercase tracking-wider border-b border-white/[0.04]">
                    <th className="text-left px-5 py-3 font-medium">Employe</th>
                    <th className="text-center px-3 py-3 font-medium">Statut</th>
                    <th className="text-center px-3 py-3 font-medium">
                      <div className="flex items-center justify-center gap-1"><LogIn className="w-3 h-3" /> Arrivee</div>
                    </th>
                    <th className="text-center px-3 py-3 font-medium">
                      <div className="flex items-center justify-center gap-1"><Pause className="w-3 h-3" /> Pause matin</div>
                    </th>
                    <th className="text-center px-3 py-3 font-medium">
                      <div className="flex items-center justify-center gap-1"><Coffee className="w-3 h-3" /> Pause midi</div>
                    </th>
                    <th className="text-center px-3 py-3 font-medium">
                      <div className="flex items-center justify-center gap-1"><Play className="w-3 h-3" /> Reprise</div>
                    </th>
                    <th className="text-center px-3 py-3 font-medium">
                      <div className="flex items-center justify-center gap-1"><LogOut className="w-3 h-3" /> Depart</div>
                    </th>
                    <th className="text-center px-3 py-3 font-medium">Deductions</th>
                    <th className="text-right px-5 py-3 font-medium">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.04]">
                  {sortedEmployees.map(emp => {
                    const cfg = STATUS_CONFIG[emp.status] || STATUS_CONFIG.absent;
                    const breaks = emp.breaks || [];

                    // Classify breaks: morning (before 11:30) vs lunch (11:30+)
                    const morningBreak = breaks.find(b => {
                      if (!b.start_time) return false;
                      try {
                        const h = parseISO(b.start_time).getHours();
                        return h < 12;
                      } catch { return false; }
                    });
                    const lunchBreak = breaks.find(b => {
                      if (!b.start_time) return false;
                      try {
                        const h = parseISO(b.start_time).getHours();
                        return h >= 12;
                      } catch { return false; }
                    });

                    // After lunch break = resume time
                    const resumeTime = lunchBreak?.end_time || morningBreak?.end_time;

                    // Auto deductions summary
                    const deductions = emp.auto_deductions || [];
                    const totalDeductMin = deductions.reduce((sum, d) => sum + (d.minutes || 0), 0);

                    return (
                      <tr key={emp.employee_id} className="hover:bg-white/[0.02] transition-colors" data-testid={`pointage-row-${emp.employee_id}`}>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${cfg.bg} ${cfg.text}`}>
                              {emp.name?.charAt(0)?.toUpperCase()}
                            </div>
                            <div>
                              <p className="text-white font-medium">{emp.name}</p>
                              <p className="text-xs text-slate-500">{emp.role}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${cfg.color}`} />
                            {cfg.label}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-center font-mono text-slate-300 text-xs">{fmtTime(emp.clock_in)}</td>
                        <td className="px-3 py-3 text-center font-mono text-xs">
                          {morningBreak ? (
                            <span className="text-amber-300">
                              {fmtTime(morningBreak.start_time)}
                              {morningBreak.end_time ? ` - ${fmtTime(morningBreak.end_time)}` : ' ...'}
                            </span>
                          ) : <span className="text-slate-600">-</span>}
                        </td>
                        <td className="px-3 py-3 text-center font-mono text-xs">
                          {lunchBreak ? (
                            <span className="text-orange-300">
                              {fmtTime(lunchBreak.start_time)}
                              {lunchBreak.end_time ? ` - ${fmtTime(lunchBreak.end_time)}` : ' ...'}
                            </span>
                          ) : <span className="text-slate-600">-</span>}
                        </td>
                        <td className="px-3 py-3 text-center font-mono text-slate-300 text-xs">{fmtTime(resumeTime)}</td>
                        <td className="px-3 py-3 text-center font-mono text-slate-300 text-xs">{fmtTime(emp.clock_out)}</td>
                        <td className="px-3 py-3 text-center text-xs">
                          {totalDeductMin > 0 ? (
                            <span className="text-rose-400 font-medium" title={deductions.map(d => d.label).join(', ')}>
                              -{totalDeductMin}min
                            </span>
                          ) : <span className="text-slate-600">-</span>}
                        </td>
                        <td className="px-5 py-3 text-right font-mono text-white text-sm font-bold">
                          {emp.total_hours != null ? `${emp.total_hours.toFixed(1)}h` : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {employees.length === 0 && (
                <div className="text-center py-10">
                  <Clock className="w-10 h-10 text-slate-700 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">Aucun employe actif</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ===== AGENDA TAB ===== */}
        {activeTab === 'agenda' && (
          <div
            className="rounded-2xl border border-white/[0.06] overflow-hidden"
            style={{ background: 'rgba(30, 41, 59, 0.5)', backdropFilter: 'blur(12px)' }}
            data-testid="agenda-section"
          >
            <div className="px-5 py-4 border-b border-white/[0.06]">
              <div className="flex items-center gap-2">
                <CalendarDays className="w-4 h-4 text-cyan-400" />
                <h3 className="text-sm font-bold text-white uppercase tracking-wider">Rendez-vous du jour</h3>
                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400">{appointments.length}</span>
              </div>
            </div>
            {appointments.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 p-5">
                {appointments.map(appt => (
                  <AppointmentCard key={appt.id} appt={appt} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <CalendarDays className="w-12 h-12 text-slate-700 mx-auto mb-3" />
                <p className="text-sm text-slate-500">Aucun rendez-vous aujourd'hui</p>
              </div>
            )}
          </div>
        )}

        {/* ===== SUMMARIES TAB ===== */}
        {activeTab === 'summaries' && (
          <>
            <div className="flex items-center gap-2 mb-2">
              <button
                onClick={() => setSummaryView('weekly')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                  summaryView === 'weekly' ? 'bg-white/[0.08] text-white' : 'text-slate-500 hover:text-slate-300'
                }`}
                data-testid="summary-weekly-btn"
              >
                Cette semaine
              </button>
              <button
                onClick={() => setSummaryView('monthly')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                  summaryView === 'monthly' ? 'bg-white/[0.08] text-white' : 'text-slate-500 hover:text-slate-300'
                }`}
                data-testid="summary-monthly-btn"
              >
                Ce mois
              </button>
            </div>

            <div
              className="rounded-2xl border border-white/[0.06] overflow-hidden"
              style={{ background: 'rgba(30, 41, 59, 0.5)', backdropFilter: 'blur(12px)' }}
              data-testid="summaries-section"
            >
              <div className="px-5 py-4 border-b border-white/[0.06]">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-indigo-400" />
                  <h3 className="text-sm font-bold text-white uppercase tracking-wider">
                    Resume {summaryView === 'weekly' ? 'hebdomadaire' : 'mensuel'}
                  </h3>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="summary-table">
                  <thead>
                    <tr className="text-xs text-slate-500 uppercase tracking-wider border-b border-white/[0.04]">
                      <th className="text-left px-5 py-3 font-medium">Employe</th>
                      <th className="text-center px-3 py-3 font-medium">Jours travailles</th>
                      <th className="text-center px-3 py-3 font-medium">Heures totales</th>
                      <th className="text-center px-3 py-3 font-medium">Moyenne / jour</th>
                      {summaryView === 'weekly' && <th className="text-center px-3 py-3 font-medium">Heures attendues</th>}
                      {summaryView === 'weekly' && <th className="text-right px-5 py-3 font-medium">Ecart</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.04]">
                    {(summaryView === 'weekly' ? weeklySummary : monthlySummary).map(row => {
                      const diff = summaryView === 'weekly' ? row.total_hours - (row.expected_weekly || 0) : null;
                      return (
                        <tr key={row.employee_id} className="hover:bg-white/[0.02] transition-colors" data-testid={`summary-row-${row.employee_id}`}>
                          <td className="px-5 py-3 text-white font-medium">{row.name}</td>
                          <td className="px-3 py-3 text-center text-slate-300">{row.days_worked}</td>
                          <td className="px-3 py-3 text-center font-mono text-white font-semibold">{row.total_hours}h</td>
                          <td className="px-3 py-3 text-center font-mono text-slate-300">{row.avg_hours}h</td>
                          {summaryView === 'weekly' && (
                            <td className="px-3 py-3 text-center font-mono text-slate-400">{row.expected_weekly}h</td>
                          )}
                          {summaryView === 'weekly' && (
                            <td className={`px-5 py-3 text-right font-mono font-semibold ${diff >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                              {diff >= 0 ? '+' : ''}{diff?.toFixed(1)}h
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {(summaryView === 'weekly' ? weeklySummary : monthlySummary).length === 0 && (
                  <div className="text-center py-10">
                    <TrendingUp className="w-10 h-10 text-slate-700 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">Aucune donnee disponible</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* ===== SATISFACTION TAB ===== */}
        {activeTab === 'satisfaction' && (
          <>
            {/* Stats row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="satisfaction-stats">
              <StatCard icon={Star} value={satisfaction.avg_rating || '-'} label="Note moyenne" accent="text-amber-400" />
              <StatCard icon={CheckCircle2} value={satisfaction.completed || 0} label="Avis recus" accent="text-emerald-400" />
              <StatCard icon={Timer} value={`${satisfaction.response_rate || 0}%`} label="Taux de reponse" accent="text-cyan-400" />
              <StatCard icon={Clock} value={satisfaction.pending || 0} label="En attente" accent="text-rose-400" />
            </div>

            {/* Rating distribution */}
            <div
              className="rounded-2xl border border-white/[0.06] p-5"
              style={{ background: 'rgba(30, 41, 59, 0.5)', backdropFilter: 'blur(12px)' }}
              data-testid="rating-distribution"
            >
              <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4">Distribution des notes</h3>
              <div className="space-y-3">
                {[5, 4, 3, 2, 1].map(star => {
                  const count = satisfaction.rating_distribution?.[star] || 0;
                  const total = satisfaction.completed || 1;
                  const pct = Math.round((count / total) * 100);
                  return (
                    <div key={star} className="flex items-center gap-3" data-testid={`rating-bar-${star}`}>
                      <div className="flex items-center gap-1 w-16 shrink-0">
                        <span className="text-sm text-white font-semibold">{star}</span>
                        <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                      </div>
                      <div className="flex-1 h-3 bg-white/[0.06] rounded-full overflow-hidden">
                        <div
                          className="h-full bg-amber-500 rounded-full transition-all duration-500"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="text-xs text-slate-400 w-14 text-right font-mono">{count} ({pct}%)</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Recent feedbacks */}
            <div
              className="rounded-2xl border border-white/[0.06] overflow-hidden"
              style={{ background: 'rgba(30, 41, 59, 0.5)', backdropFilter: 'blur(12px)' }}
              data-testid="recent-feedbacks"
            >
              <div className="px-5 py-4 border-b border-white/[0.06]">
                <h3 className="text-sm font-bold text-white uppercase tracking-wider">Derniers avis</h3>
              </div>
              {satisfaction.recent?.length > 0 ? (
                <div className="divide-y divide-white/[0.04]">
                  {satisfaction.recent.map((fb, i) => (
                    <div key={i} className="px-5 py-4" data-testid={`feedback-item-${i}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-1">
                            <p className="text-sm font-semibold text-white">{fb.client_name}</p>
                            <RatingStars rating={fb.rating} size="w-3.5 h-3.5" />
                          </div>
                          {fb.vehicle_info && (
                            <p className="text-xs text-slate-500 mb-1">{fb.vehicle_info}</p>
                          )}
                          {fb.comment && (
                            <p className="text-sm text-slate-300 mt-1">{fb.comment}</p>
                          )}
                        </div>
                        {fb.completed_at && (
                          <span className="text-xs text-slate-600 shrink-0">
                            {(() => { try { return format(parseISO(fb.completed_at), 'd MMM', { locale: fr }); } catch { return ''; }})()}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-10">
                  <Star className="w-10 h-10 text-slate-700 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">Aucun avis pour le moment</p>
                </div>
              )}
            </div>
          </>
        )}

      </div>
    </div>
  );
};

const AppointmentCard = ({ appt }) => {
  const statusStyles = {
    scheduled: 'bg-blue-500/15 text-blue-400',
    in_progress: 'bg-amber-500/15 text-amber-400',
    completed: 'bg-emerald-500/15 text-emerald-400',
    cancelled: 'bg-red-500/15 text-red-400',
  };
  const statusLabels = {
    scheduled: 'Planifie',
    in_progress: 'En cours',
    completed: 'Termine',
    cancelled: 'Annule',
  };

  return (
    <div
      className="p-4 rounded-xl border border-white/[0.06] transition-all hover:border-white/10"
      style={{ background: 'rgba(30, 41, 59, 0.6)' }}
      data-testid={`appointment-card-${appt.id}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {appt.time && <span className="text-sm text-cyan-400 font-mono font-semibold">{appt.time}</span>}
        </div>
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${statusStyles[appt.status] || statusStyles.scheduled}`}>
          {statusLabels[appt.status] || appt.status}
        </span>
      </div>
      <p className="text-sm font-semibold text-white">{appt.client_name}</p>
      {appt.vehicle_info && (
        <p className="text-xs text-slate-400 mt-1">{appt.vehicle_info}</p>
      )}
      {appt.client_phone && (
        <div className="flex items-center gap-1.5 mt-2">
          <Phone className="w-3 h-3 text-slate-500" />
          <a href={`tel:${appt.client_phone}`} className="text-xs text-slate-400 hover:text-white transition-colors">{appt.client_phone}</a>
        </div>
      )}
      {appt.description && (
        <p className="text-xs text-slate-500 mt-1.5 line-clamp-2">{appt.description}</p>
      )}
    </div>
  );
};

export default AdminMobileDashboard;
